import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventStyleArgs, SlotClassArgs } from '@progress/kendo-angular-scheduler';
import { gearIcon, reorderIcon } from '@progress/kendo-svg-icons';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map, switchMap, BehaviorSubject } from 'rxjs';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { IHoliday } from 'src/app/setting/holiday/model/holiday';
import { HolidayService } from 'src/app/setting/holiday/services/holiday.service';
import { IRessource } from 'src/app/setting/models/ressource';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { getCalendarWeekFromDate } from 'src/app/shared/helpers/date-helpers';
import { findHoliday } from 'src/app/shared/helpers/holiday';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { AbsenceState, AbsenceType, IAbsenceGroup, IAbsencePlanning, ISearchAbsenceGroupsRequest } from 'src/app/time-tracking/models/absence-group';
import { AbsenceGroupService } from 'src/app/time-tracking/services/absence-group.service';
import { IDashboardWidgetPref } from 'src/app/user-settings/models/user-settings';
import { UserSettingsStoreService } from 'src/app/user-settings/services/user-settings-store.service';
import {
  ISchedulerItem,
  ISearchSchedulerItemRequest,
} from '../../scheduler/models/scheduler';
import { ResourcePlanerService } from '../../scheduler/services/resource-planer.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';

// Keys are persisted in UserSettings.hiddenDashboardWidgets and used as i18n keys (home.widgets.<key>).
type DashboardWidget = 'calendar' | 'nextHolidays' | 'myBalances' | 'myNextAbsences' | 'absentToday';

@UntilDestroy()
@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit {
  constructor(
    private schedulerService: ResourcePlanerService,
    private route: ActivatedRoute,
    private employeeService: EmployeeService,
    private holidayService: HolidayService,
    private absenceGroupService: AbsenceGroupService,
    private tenantSettingsStore: TenantSettingsStoreService,
    private permissionStore: PermissionStorageService,
    private store: Store<AppStateInterface>,
    private userSettingsStore: UserSettingsStoreService
  ) {}

  employee: IEmployee;

  ressource: IRessource;

  holidays: IHoliday[];

  weekNumber:number = getCalendarWeekFromDate(new Date());
  weekNumberEnd:number;
  public toolbarWidth: number;

  events$ = new BehaviorSubject<ISchedulerItem[]>([]);
  absenceGroups$ = new BehaviorSubject<IAbsenceGroup[]>([]);
  tenantSettings$ = this.tenantSettingsStore.tenantSettings$;
  currentEmployee$ = this.store.select(currentEmployeeSelector);

  isLoading = false;

  // Widgets in default order and the right needed to see them; widgets without a right are always available.
  widgets: { key: DashboardWidget, permission?: string }[] = [
    { key: 'calendar', permission: 'Permissions.SchedulerItems.Search' },
    { key: 'nextHolidays', permission: 'Permissions.Holidays.Search' },
    { key: 'myBalances' },
    { key: 'myNextAbsences', permission: 'Permissions.AbsenceGroups.Search' },
    { key: 'absentToday', permission: 'Permissions.SchedulerItems.Search' },
  ];
  availableWidgets = this.widgets.filter(widget => !widget.permission || this.hasPermission(widget.permission));
  // Available widgets in the user's saved order with visibility (rendered in this order).
  orderedWidgets: IDashboardWidgetPref[] = this.applyPrefs([]);

  gearIcon = gearIcon;
  dragHandleIcon = reorderIcon;
  widgetDialogOpened = false;
  draftWidgets: IDashboardWidgetPref[] = [];
  private dragIndex: number | null = null;
  dragOverIndex: number | null = null;

  nextHolidays: IHoliday[] = [];
  nextAbsences: IAbsenceGroup[] = [];
  absentToday: IAbsencePlanning[] = [];

  get absenceStates() {
    return AbsenceState;
  }


  get absenceTypes() {
    return AbsenceType;
  }

  listSchedulerItemsRequest: ISearchSchedulerItemRequest = {
    ressourceId: null,
    start: new Date(),
    pageNumber: 0,
    pageSize: 999999,
  };

  listHolidayRequest: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 999999,
    advancedFilter: {
      logic: 'and',
      filters: [
        {
          field: 'start',
          operator: 'gte',
          value: new Date(new Date().setUTCHours(0, 0, 0, 0)),
        },
        {
          field: 'end',
          operator: 'lte',
          value: new Date(new Date().setUTCHours(23, 59, 59, 999)),
        },
      ],
    },
  };

  listAbsenceGroupRequest : ISearchAbsenceGroupsRequest = {
    pageNumber: 0,
    pageSize: 999999,
    start: new Date(new Date().setUTCHours(0,0,0,0)),
    end: new Date(new Date().setUTCHours(23,59,59,999))
  }

  ngOnInit(): void {
    // this.employee = this.route.snapshot.data["employee"] as IEmployee;
    this.loadCurrentEmployee();

    this.userSettingsStore.userSettings$
      .pipe(untilDestroyed(this))
      .subscribe(settings => this.orderedWidgets = this.applyPrefs(settings?.dashboardWidgets ?? []));

    this.loadNextHolidays();
    this.loadAbsentToday();
  }

  loadCurrentEmployee() {
    this.employeeService.getEmployeeDetails().subscribe((response) => {
      this.employee = response;
      this.listSchedulerItemsRequest.ressourceId = this.employee.ressource.id;
      this.listAbsenceGroupRequest.employeeId = this.employee.id;
      this.loadNextAbsences();
    });
  }

  public selectedDate: Date = new Date();

  // --- Widget order and visibility ---------------------------------------

  // Saved prefs first (in their order, unknown keys and widgets without the right dropped),
  // then any available widget not yet in the prefs, visible, in default order.
  private applyPrefs(prefs: IDashboardWidgetPref[]): IDashboardWidgetPref[] {
    const available: string[] = this.availableWidgets.map(widget => widget.key);
    const known = prefs.filter(pref => available.includes(pref.key));
    const missing = available
      .filter(key => !known.some(pref => pref.key === key))
      .map(key => ({ key, visible: true }));
    return [...known, ...missing];
  }

  openWidgetDialog() {
    this.draftWidgets = this.orderedWidgets.map(widget => ({ ...widget }));
    this.widgetDialogOpened = true;
  }

  toggleWidget(index: number, checked: boolean) {
    this.draftWidgets[index].visible = checked;
  }

  saveWidgets() {
    this.userSettingsStore.updateDashboardWidgets(this.draftWidgets);
    this.widgetDialogOpened = false;
  }

  // Drag-and-drop reordering in the dialog, same pointer-based approach as the assignment column picker.
  onPointerDown(index: number, event: PointerEvent): void {
    if (event.button !== undefined && event.button !== 0) return;
    this.dragIndex = index;
    this.dragOverIndex = index;
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onPointerMove(event: PointerEvent): void {
    if (this.dragIndex === null) return;
    event.preventDefault();
    const item = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-widget-idx]') as HTMLElement | null;
    if (!item) return;
    const idx = Number(item.dataset['widgetIdx']);
    if (!Number.isNaN(idx)) this.dragOverIndex = idx;
  }

  onPointerUp(): void {
    if (this.dragIndex !== null && this.dragOverIndex !== null && this.dragIndex !== this.dragOverIndex) {
      const moved = this.draftWidgets.splice(this.dragIndex, 1)[0];
      this.draftWidgets.splice(this.dragOverIndex, 0, moved);
    }
    this.dragIndex = null;
    this.dragOverIndex = null;
  }

  // --- Widget data (loaded once per visit) --------------------------------

  private startOfToday(): Date {
    return new Date(new Date().setUTCHours(0, 0, 0, 0));
  }

  private loadNextHolidays() {
    if (!this.hasPermission('Permissions.Holidays.Search')) {
      return;
    }

    this.holidayService.searchHolidays({
      pageNumber: 0,
      pageSize: 3,
      advancedFilter: {
        logic: 'and',
        filters: [{ field: 'start', operator: 'gte', value: this.startOfToday() }],
      },
    })
      .pipe(untilDestroyed(this))
      .subscribe(response => this.nextHolidays = response.data);
  }

  private loadNextAbsences() {
    if (!this.hasPermission('Permissions.AbsenceGroups.Search')) {
      return;
    }

    this.absenceGroupService.searchAbsenceGroups({
      pageNumber: 0,
      pageSize: 10,
      employeeId: this.employee.id,
      start: this.startOfToday(),
    })
      .pipe(untilDestroyed(this))
      .subscribe(response => this.nextAbsences = response.data
        .filter(absence => absence.absenceState !== AbsenceState.Declined)
        .slice(0, 3));
  }

  private loadAbsentToday() {
    if (!this.hasPermission('Permissions.SchedulerItems.Search')) {
      return;
    }

    this.schedulerService.listAbsences({
      pageNumber: 0,
      pageSize: 999999,
      start: this.startOfToday(),
      end: new Date(new Date().setUTCHours(23, 59, 59, 999)),
    })
      .pipe(untilDestroyed(this))
      .subscribe(response => this.absentToday = response.data
        .filter(absence => absence.absenceState !== AbsenceState.Declined && absence.employeeId !== this.employee?.id));
  }

  absenceTypeName(type: AbsenceType): string {
    return translate('absenceGroups.absenceTypes.' + AbsenceType[type]);
  }

  absenceStateName(state: AbsenceState): string {
    return translate('absenceGroups.absenceStates.' + AbsenceState[state]);
  }

  // --- Scheduler ----------------------------------------------------------

  loadEvents() {
    Promise.resolve().then(() => this.isLoading = true);
    this.listAbsenceGroupRequest.start = this.listSchedulerItemsRequest.start;
    this.listAbsenceGroupRequest.end = this.listSchedulerItemsRequest.end;

    if (this.hasPermission('Permissions.Holidays.Search')) {
      this.listHolidayRequest.advancedFilter.filters[0].value =
        this.listSchedulerItemsRequest.start;
      this.listHolidayRequest.advancedFilter.filters[1].value =
        this.listSchedulerItemsRequest.end;

      this.holidayService
        .searchHolidays(this.listHolidayRequest)
        .pipe(untilDestroyed(this))
        .subscribe((response) => {
          this.holidays = response.data;
        });
    }

    this.schedulerService
      .listEvents(this.listSchedulerItemsRequest)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.events$.next(response.data);
        this.isLoading = false;
      });

    // Own absences only (employeeId is the current employee), so the base right suffices.
    if (this.hasPermission('Permissions.AbsenceGroups.Search')) {
      this.absenceGroupService.searchAbsenceGroups(this.listAbsenceGroupRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.absenceGroups$.next(response.data);
      })
    }
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }

  onDateChange(event) {
    this.listSchedulerItemsRequest.start = event.dateRange.start;
    this.listSchedulerItemsRequest.end = event.dateRange.end;
    this.loadEvents();

    this.weekNumber = getCalendarWeekFromDate(event.dateRange.start);
    this.weekNumberEnd = getCalendarWeekFromDate(event.dateRange.end);
  }

  setSlotBackgroundColor = (args: SlotClassArgs) => {
    if (findHoliday(this.holidays, args.start)) {
      return 'holiday';
    }
  };

  // Shown under the date in the date header so users see why a day is red.
  holidayName = (date: Date): string | undefined => findHoliday(this.holidays, date)?.name;

  schedulerEvents$: Observable<ISchedulerItem[]> = combineLatest([
    this.events$,
    this.absenceGroups$,
    this.tenantSettings$
  ]).pipe(
    map(([events, absenceGroups, tenantSettings]) => ({
      events,
      absenceGroups,
      tenantSettings
    })),
    switchMap(({ events, absenceGroups, tenantSettings }) => {
      let schedulerEvents: ISchedulerItem[] = [];


      events.map((event) => {
        for(var loopDate = new Date(event.start); loopDate <= new Date(event.end);loopDate.setDate(loopDate.getDate() + 1)){

          const newEvent: ISchedulerItem = {
            title: event.title,
            start: new Date(event.start).setHours(0,0,0,0) == new Date(loopDate).setHours(0,0,0,0) ? new Date(event.start) : new Date(new Date(loopDate).setHours(0,0,0,0)),
            end: new Date(event.end).setHours(0,0,0,0) == new Date(loopDate).setHours(0,0,0,0) ? new Date(event.end) : new Date(new Date(loopDate).setHours(23, 59, 59, 999)),
            ressourceIds: event.ressourceIds,
            ressources: event.ressources,
            description: event.description,
            isAllDay: event.isAllDay,
            recurrenceRule: event.recurrenceRule,
            timeEntryCategoryId: event.timeEntryCategoryId,
            timeEntryCategory: event.timeEntryCategory,
            employeeIds: event.employeeIds,
            employees: event.employees,
            assignmentId: event.assignmentId,
            assignment: event.assignment,
            id: event.id
          };

          schedulerEvents.push(newEvent);
        }

      });

      absenceGroups.map((absenceGroup) => {
        if (absenceGroup.absenceState != this.absenceStates.Declined) {

          absenceGroup.absences.forEach(absence => {

            let startTime = new Date(absence.date);
            let endTime = new Date(
              new Date(absence.date).setUTCHours(23, 59, 59, 999)
            );

             // absence in the morning
            if(absence.isHalfDayEnd){
              const dayEndTimeSpanObj = TimeSpan.parse(tenantSettings.endMorningWorkTime).getAsTimeObject();
              endTime = new Date(
                new Date(absenceGroup.dateTo).setHours(dayEndTimeSpanObj.hours, dayEndTimeSpanObj.minutes)
              );
            }
            if ( absence.isHalfDayStart){
              // absence in the afternoon
              const dayStartTimeSpanObj = TimeSpan.parse(tenantSettings.startAfternoonWorkTime).getAsTimeObject();
              startTime = new Date(
                new Date(absenceGroup.dateFrom).setHours(dayStartTimeSpanObj.hours, dayStartTimeSpanObj.minutes)
              );
            }

            const newEvent: ISchedulerItem = {
              title: `${translate(
                'absenceGroups.absenceTypes.' +
                  this.absenceTypes[absenceGroup.absenceType]
              )} (${translate(
                'absenceGroups.absenceStates.' +
                  this.absenceStates[absenceGroup.absenceState]
              )})
               ${absenceGroup?.title ?? ''}`,
              start: startTime,
              end: endTime,
              description: `${translate(
                'absenceGroups.absenceStates.' +
                  this.absenceStates[absenceGroup.absenceState]
              )}
               ${absenceGroup?.title ?? ''}`,
              employeeIds: [absenceGroup.employee.id],
              employees: [absenceGroup.employee],
              ressourceIds: [absenceGroup.employee.ressource.id],
              ressources: [absenceGroup.employee.ressource],
              absenceType: absenceGroup.absenceType,
              id: absenceGroup.id
            };

            schedulerEvents.push(newEvent);
          });
        }
      });

      return [schedulerEvents];
    })
  );

  setBorderColor = (args: EventStyleArgs) => {
    let ngStyleObj: Object;

      if (args.event.dataItem?.absenceType == this.absenceTypes.Compensation) {
        ngStyleObj = {
          'background-color': '#fd9f3e',
        };
      } else if (args.event.dataItem?.absenceType != null) {
        ngStyleObj = {
          'background-color': 'red',
        };
      } else {
        ngStyleObj = {
          'background-color': `${args.event.dataItem?.timeEntryCategory?.color}`,
        };
    }

    return ngStyleObj;
  };

  public onSchedulerResize(width: number): void {
    this.toolbarWidth = width;
  }
}
